// The Dharma app, as the public can install it today.
//
// Every line here was supplied by the app thread on 2026-10-06 and is true of
// the build in the stores (1.0.9). TestFlight-only features are deliberately
// absent. Rules that apply to this copy, from Anant:
//   - no tip jar, donations or "support us" near the app
//   - no streak or loss language, no urgency, no claims of results or blessings
//   - no "Hindu" in headings (it left the visible store name on 2 Oct 2026)
//   - no rating shown; say nothing stronger than "no ads, no sign-up"
//   - never "mythology"
//   - the centre of any composite is Krishna (Vasudeva) or Shiva (Anant, 6 Oct)

const BASE =
  "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/app";
const IMG = `${BASE}/v1`; // icon
const NEW = `${BASE}/v2`; // screens captured from the public build on 6 Oct 2026

// Not used, on the app thread's advice: the Gita fill-the-blanks screen (Anant
// rejected it for the store set), the Gita lesson screen (its English meaning is
// close to a published translation), and the Buddhist temple (unreleased work
// on that screen; the path itself is live and is described in words).
const S = {
  krishna: {
    src: `${NEW}/01_mandir_krishna.jpg`,
    alt: "The temple screen: Krishna with his flute, the aarti plate and the mantra Om Namo Bhagavate Vasudevaya",
  },
  shiva: {
    src: `${NEW}/02_mandir_shiva.jpg`,
    alt: "The temple screen: Shiva seated in meditation, the aarti plate and the mantra Om Namah Shivaya",
  },
  today: {
    src: `${NEW}/03_today.jpg`,
    alt: "The Today screen: the date and tithi, the day's verse, the lamps and the day's darshan",
  },
  card: {
    src: `${NEW}/19_darshan_card.jpg`,
    alt: "The day's darshan card: a painting of Vishnu with a verse from the Katha Upanishad, and buttons to share or save it",
  },
  practice: {
    src: `${NEW}/09_practice_hub.jpg`,
    alt: "The Practice screen: japa, meditation, Mantra Vidya, Navratri and learning by heart",
  },
  gitaPath: {
    src: `${NEW}/10_gita_path.jpg`,
    alt: "Bhagavad Gita Essentials: twelve verses listed, three of them learned by heart",
  },
  sutras: {
    src: `${NEW}/15_yoga_sutras.jpg`,
    alt: "The first Yoga Sutra of Patanjali as a short lesson: listen, echo it, and read the meaning",
  },
  japa: {
    src: `${NEW}/13_japa_mala.jpg`,
    alt: "The japa screen: Om, with the count at 37 of 108",
  },
  dhyana: {
    src: `${NEW}/14_dhyana.jpg`,
    alt: "The Meditation Room: guided sits of 7 to 25 minutes, in English or Hindi",
  },
  vidya: {
    src: `${NEW}/05_mantra_vidya.jpg`,
    alt: "A Mantra Vidya lesson on the Gayatri mantra, with its meaning in English and Hindi",
  },
  navratri: {
    src: `${NEW}/06_navaratri_night.jpg`,
    alt: "A Navratri night: Shailaputri, with her story in English and Hindi",
  },
  library: {
    src: `${NEW}/07_library.jpg`,
    alt: "The Library screen: Varaha, Samudra Manthan, the illustrated Hanuman Chalisa and darshan wallpapers",
  },
  varaha: {
    src: `${NEW}/17_story_varaha.jpg`,
    alt: "A scene from Varaha: two great hands closing round the Earth, with the line of the story beneath",
  },
  wallpapers: {
    src: `${NEW}/18_wallpapers.jpg`,
    alt: "Darshan wallpapers: Hanuman across the moon, Shiva, Lakshmi on the lotus and Krishna at dusk",
  },
  manthan: {
    src: `${NEW}/16_story_samudra_manthan.jpg`,
    alt: "The opening scene of Samudra Manthan: Vishnu above the great tortoise in the cosmic ocean",
  },
  chalisa: {
    src: `${NEW}/04_hanuman_chalisa.jpg`,
    alt: "The illustrated Hanuman Chalisa: Hanuman painted against a starry sky, with the verse and its meaning",
  },
};

export const dharmaApp = {
  name: "Dharma — Gita, Chalisa, Mantra",
  short: "Dharma",
  tagline:
    "Offer the aarti in a temple of painted deities, read the illustrated Hanuman Chalisa, and learn the Gita by heart, one verse a day.",
  price: "Free. No ads. No sign-up.",
  platforms: "iPhone and Android",
  icon: `${IMG}/icon.png`,
  stores: {
    ios: "https://apps.apple.com/app/id6777929290",
    android: "https://play.google.com/store/apps/details?id=com.taraventures.dharma",
  },

  // Left, centre, right: learn by heart, the temple, the painted stories.
  // The centre is Krishna (Anant: Krishna or Shiva in the middle).
  trio: [S.chalisa, S.krishna, S.manthan],

  // The app has four tabs. The showcase walks through all four.
  rooms: [
    {
      id: "mandir",
      label: "Mandir",
      title: "A temple in your hand",
      body:
        "Swipe through painted deities: Krishna, Shiva, Ganesha, Hanuman, Lakshmi, Ram, Saraswati and more. Offer the aarti with the plate and its lamp, sound the conch, and hear each deity's mantra.",
    },
    {
      id: "today",
      label: "Today",
      title: "A new darshan every day",
      body:
        "A devotional painting with a verse and its exact source, to keep or to share. Today also shows the tithi and the festival.",
    },
    {
      id: "practice",
      label: "Practice",
      title: "Learn it by heart",
      body:
        "Key verses of the Bhagavad Gita, each narrated in Sanskrit and explained in English or Hindi, with the Hanuman Chalisa, the Upanishads and the Yoga Sutras of Patanjali. A mala of 108 for japa, guided meditations of 7 to 25 minutes, and Mantra Vidya for what your mantras mean.",
    },
    {
      id: "library",
      label: "Library",
      title: "Painted stories, free",
      body:
        "Graphic novels to read scene by scene: Varaha, Samudra Manthan and the illustrated Hanuman Chalisa with every verse painted. Darshan wallpapers for your lock screen.",
    },
  ],

  // No screen from the hero is in the row. The chips do the jumping.
  screens: [
    { ...S.shiva, room: "mandir", caption: "Offer the aarti, sound the conch" },
    { ...S.today, room: "today", caption: "The day's verse, tithi and festival" },
    { ...S.card, room: "today", caption: "A darshan card to keep or share" },
    { ...S.practice, room: "practice", caption: "Japa, meditation and study in one place" },
    { ...S.gitaPath, room: "practice", caption: "The Gita by heart, one verse a day" },
    { ...S.sutras, room: "practice", caption: "Each verse narrated and explained" },
    { ...S.japa, room: "practice", caption: "A mala of 108" },
    { ...S.dhyana, room: "practice", caption: "Guided meditations, 7 to 25 minutes" },
    { ...S.vidya, room: "practice", caption: "Know what your mantras mean" },
    { ...S.navratri, room: "practice", caption: "Navratri, the nine forms of Durga" },
    { ...S.library, room: "library", caption: "Painted stories, free" },
    { ...S.varaha, room: "library", caption: "Varaha, told in fifty paintings" },
    { ...S.wallpapers, room: "library", caption: "Darshan wallpapers for your lock screen" },
  ],

  also:
    "There is a Buddhist path too, with its own temple of Buddhas and Bodhisattvas, the Dhammapada, a Zen course and The Life of the Buddha in 24 painted scenes.",
};
